import {Combobox, TagsInput, useCombobox, useListCollection, useTagsInput} from "@chakra-ui/react";
import {forwardRef, useCallback, useEffect, useId, useMemo} from "react";
import useTranslate from "platformed-components/i18n/useTranslate";
import {Portal} from "../Portal";
import {SetValueInputProps} from "./types";
import {useCommitUncommitted} from "./setInputHelpers";

/**
 * Integer set (multi-value) input component
 *
 * Features:
 * - Tags-based UI for multiple integer values
 * - Optional predefined options in dropdown
 * - Validates input is a valid integer
 * - Deduplicates values
 */
const IntegerSetInput = forwardRef<HTMLInputElement, SetValueInputProps<number>>(
  ({value, onChange, options = [], placeholder, disabled = false}, ref) => {
    const t = useTranslate();
    // Current values
    const values = useMemo(() => (Array.isArray(value) ? value : []), [value]);
    const stringValues = useMemo(() => values.map(v => String(v)), [values]);

    type CollectionItem = {label: string; value: string};
    const createCollectionItems = useCallback(
      (opts: number[], selectedValues: number[]): CollectionItem[] =>
        opts.filter(opt => !selectedValues.includes(opt)).map(opt => ({label: String(opt), value: String(opt)})),
      [],
    );

    // Collection of options - exclude already selected values
    const {collection, set} = useListCollection({initialItems: createCollectionItems(options, values)});

    // Reset collection when values change
    useEffect(() => {
      set(createCollectionItems(options, values));
    }, [values, options, set, createCollectionItems]);

    const uid = useId();

    const parseValue = useCallback((input: string): number | null => {
      const trimmed = input.trim();
      if (trimmed === "") return null;
      const num = parseInt(trimmed, 10);
      return isNaN(num) ? null : num;
    }, []);

    const tags = useTagsInput({
      ids: {input: `input_${uid}`, control: `control_${uid}`},
      value: stringValues,
      onValueChange: details => {
        // Parse strings to integers, filter out invalid and duplicates
        const parsedValues = details.value.map(parseValue).filter((v): v is number => v !== null);
        const uniqueValues = [...new Set(parsedValues)];
        onChange(uniqueValues.length > 0 ? uniqueValues : null);
      },
      // Validate that input is a valid integer
      validate: details => parseValue(details.inputValue) !== null,
      disabled,
    });

    const combobox = useCombobox({
      ids: {input: `input_${uid}`, control: `control_${uid}`},
      collection,
      value: [],
      allowCustomValue: true,
      onValueChange: e => {
        const selectedValue = e.value[0];
        if (selectedValue) {
          const parsed = parseValue(selectedValue);
          if (parsed !== null && !values.includes(parsed)) {
            tags.addValue(selectedValue);
          }
        }
      },
      selectionBehavior: "clear",
      openOnClick: options.length > 0,
      disabled,
    });

    const effectivePlaceholder = placeholder ?? (values.length === 0 ? t("c.Enter_numbers[ellipsis]") : "");
    const showDropdownHint = options.length > 0;

    // Commit uncommitted input helper
    const commitUncommitted = useCommitUncommitted(tags, values, parseValue);

    return (
      <Combobox.RootProvider value={combobox}>
        <TagsInput.RootProvider value={tags}>
          <TagsInput.Control>
            {tags.value.map((tag, index) => (
              <TagsInput.Item key={index} index={index} value={tag}>
                <TagsInput.ItemPreview>
                  <TagsInput.ItemText>{tag}</TagsInput.ItemText>
                  <TagsInput.ItemDeleteTrigger />
                </TagsInput.ItemPreview>
              </TagsInput.Item>
            ))}
            <Combobox.Input unstyled asChild>
              <TagsInput.Input ref={ref} placeholder={effectivePlaceholder} onBlur={commitUncommitted} />
            </Combobox.Input>
          </TagsInput.Control>

          {showDropdownHint && (
            <Portal>
              <Combobox.Positioner>
                <Combobox.Content>
                  {collection.items.map(item => (
                    <Combobox.Item item={item} key={item.value}>
                      <Combobox.ItemText>{item.label}</Combobox.ItemText>
                      <Combobox.ItemIndicator />
                    </Combobox.Item>
                  ))}
                </Combobox.Content>
              </Combobox.Positioner>
            </Portal>
          )}
        </TagsInput.RootProvider>
      </Combobox.RootProvider>
    );
  },
);

IntegerSetInput.displayName = "IntegerSetInput";

export default IntegerSetInput;
